import {
  ONBOARDING_CARD_ESTIMATED_HEIGHT_PX,
  ONBOARDING_CARD_MAX_WIDTH_PX,
  ONBOARDING_CONNECTOR_LENGTH_PX,
  ONBOARDING_EDGE_INSET_PX,
  ONBOARDING_RING_PAD_PX,
} from './consts'
import type { OnboardingStep } from './types'

export function stepHasTooltipBody(step: OnboardingStep): boolean {
  return step.renderTooltip != null || step.content != null
}

export function findActiveStep(
  steps: OnboardingStep[],
  seen: Record<string, boolean>
): OnboardingStep | null {
  for (const step of steps) {
    if (seen[step.elementId]) continue
    if (step.requiresSeenIds?.some((id) => !seen[id])) continue
    if (step.when && !step.when()) continue
    if (!stepHasTooltipBody(step)) continue
    return step
  }
  return null
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max)

export type OnboardingCardLayout = {
  cardLeft: number
  cardTop: number
  originX: number
  originY: number
  tipX: number
  tipY: number
  side: 'top' | 'bottom' | 'left' | 'right'
}

export function computeOnboardingCardLayout(
  rect: DOMRect,
  viewport: { innerWidth: number; innerHeight: number },
  card: { width: number; height: number } = {
    width: ONBOARDING_CARD_MAX_WIDTH_PX,
    height: ONBOARDING_CARD_ESTIMATED_HEIGHT_PX,
  }
): OnboardingCardLayout {
  const originX = rect.left + rect.width / 2
  const originY = rect.top + rect.height / 2
  const gap = ONBOARDING_CONNECTOR_LENGTH_PX
  const inset = ONBOARDING_EDGE_INSET_PX
  const { innerWidth: vw, innerHeight: vh } = viewport

  const belowTop = rect.bottom + gap
  const fitsBelow = belowTop + card.height + inset <= vh
  const aboveTop = rect.top - gap - card.height
  const fitsAbove = aboveTop >= inset
  const rightLeft = rect.right + gap
  const fitsRight = rightLeft + card.width + inset <= vw
  const leftLeft = rect.left - gap - card.width
  const fitsLeft = leftLeft >= inset

  const side: OnboardingCardLayout['side'] = fitsBelow
    ? 'bottom'
    : fitsAbove
      ? 'top'
      : fitsLeft
        ? 'left'
        : fitsRight
          ? 'right'
          : 'bottom'

  let cardLeft = 0
  let cardTop = 0
  let tipX = originX
  let tipY = originY

  if (side === 'bottom') {
    cardTop = belowTop
    cardLeft = clamp(originX - card.width / 2, inset, vw - card.width - inset)
    tipX = clamp(originX, cardLeft + 20, cardLeft + card.width - 20)
    tipY = cardTop
  } else if (side === 'top') {
    cardTop = aboveTop
    cardLeft = clamp(originX - card.width / 2, inset, vw - card.width - inset)
    tipX = clamp(originX, cardLeft + 20, cardLeft + card.width - 20)
    tipY = cardTop + card.height
  } else if (side === 'left') {
    cardLeft = leftLeft
    cardTop = clamp(originY - card.height / 2, inset, vh - card.height - inset)
    tipX = cardLeft + card.width
    tipY = clamp(originY, cardTop + 20, cardTop + card.height - 20)
  } else {
    cardLeft = rightLeft
    cardTop = clamp(originY - card.height / 2, inset, vh - card.height - inset)
    tipX = cardLeft
    tipY = clamp(originY, cardTop + 20, cardTop + card.height - 20)
  }

  const maxLeft = Math.max(inset, vw - card.width - inset)
  const maxTop = Math.max(inset, vh - card.height - inset)
  cardLeft = clamp(cardLeft, inset, maxLeft)
  cardTop = clamp(cardTop, inset, maxTop)

  if (side === 'bottom') {
    tipX = clamp(originX, cardLeft + 20, cardLeft + card.width - 20)
    tipY = cardTop
  } else if (side === 'top') {
    tipX = clamp(originX, cardLeft + 20, cardLeft + card.width - 20)
    tipY = cardTop + card.height
  } else if (side === 'left') {
    tipX = cardLeft + card.width
    tipY = clamp(originY, cardTop + 20, cardTop + card.height - 20)
  } else {
    tipX = cardLeft
    tipY = clamp(originY, cardTop + 20, cardTop + card.height - 20)
  }

  return { cardLeft, cardTop, originX, originY, tipX, tipY, side }
}

export function getOnboardingRingBox(rect: DOMRect) {
  const pad = ONBOARDING_RING_PAD_PX
  return {
    left: rect.left - pad,
    top: rect.top - pad,
    width: rect.width + pad * 2,
    height: rect.height + pad * 2,
  }
}

/** Walk up from `element` and collect nodes that can scroll (nested panels, Radix viewports, etc.) */
export function getScrollableAncestors(element: HTMLElement | null): HTMLElement[] {
  const parents: HTMLElement[] = []
  let node: HTMLElement | null = element?.parentElement ?? null
  while (node) {
    const style = window.getComputedStyle(node)
    const overflow = `${style.overflow}${style.overflowY}${style.overflowX}`
    if (/(auto|scroll|overlay)/.test(overflow)) {
      parents.push(node)
    }
    node = node.parentElement
  }
  return parents
}
